import React, { useMemo, useState } from "react";
import { Platform, ScrollView, StyleSheet, View, useWindowDimensions } from "react-native";
import { SparkleIcon, XIcon } from "@phosphor-icons/react";
import { MotiView } from "moti";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { colors, radii, shadowStyles, spacing } from "../../../styles/design-system";
import ExplorationMenuItem from "./ExplorationMenuItem";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import ExplorationMediaModal from "./ExplorationMediaModal";

export type ExplorationMediaType = "video" | "image";

export interface ExplorationMediaItem {
  id: string;
  title: string;
  type: ExplorationMediaType;
  metadata: string;
  thumbnail: string;
  description?: string;
  mediaUrl?: string;
  videoId?: string;
}

interface ExplorationMenuProps {
  items: ExplorationMediaItem[];
  initialOpen?: boolean;
  onOpen?: () => void;
}

const PANEL_WIDTH = 360;
const PANEL_MAX_HEIGHT = 550;
const PANEL_HEADER_HEIGHT = 64;
const PANEL_BOTTOM_GAP = 30;

type SelectedMedia =
  | {
      type: "video";
      videoId: string;
      title?: string;
      description?: string;
    }
  | {
      type: "image";
      src: string;
      title?: string;
      description?: string;
    };

const ExplorationMenu: React.FC<ExplorationMenuProps> = ({ items, initialOpen = false, onOpen }) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [isExploreButtonHovered, setIsExploreButtonHovered] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<SelectedMedia | null>(null);
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const panelMaxHeight = Math.min(PANEL_MAX_HEIGHT, Math.max(windowHeight - spacing.sm - PANEL_BOTTOM_GAP, 0));
  const contentMaxHeight = Math.max(panelMaxHeight - PANEL_HEADER_HEIGHT, 0);
  const { isXLarge } = useResponsiveScreen();

  const openVideoModal = (item: ExplorationMediaItem) => {
    if (!item.videoId) {
      return;
    }

    setSelectedMedia({
      type: "video",
      videoId: item.videoId,
      title: item.title,
      description: item.description,
    });
  };

  const openImageModal = (item: ExplorationMediaItem) => {
    const imageSrc = item.mediaUrl ?? item.thumbnail;

    if (!imageSrc) {
      return;
    }

    setSelectedMedia({
      type: "image",
      src: imageSrc,
      title: item.title,
      description: item.description,
    });
  };

  const handleItemPress = (item: ExplorationMediaItem) => () => {
    if (item.type === "video") {
      openVideoModal(item);
      return;
    }

    if (item.type === "image") {
      openImageModal(item);
    }
  };

  const handleItemHoverIn = (itemId: string) => () => {
    if (Platform.OS === "web") {
      setHoveredItemId(itemId);
    }
  };

  const handleItemHoverOut = () => {
    if (Platform.OS === "web") {
      setHoveredItemId(null);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    onOpen?.();
  };

  const hasItems = useMemo(() => items.length > 0, [items.length]);

  return (
    <View style={styles.container}>
      {isXLarge ? (
        // Desktop: top-right "Explore" button
        <MotiView
          style={[styles.exploreButton, styles.exploreButtonCta]}
          animate={{
            opacity: isOpen ? 0 : 1,
            translateX: isOpen ? 10 : 0,
            backgroundColor: isExploreButtonHovered ? colors.grey[200] : colors.grey[100],
          }}
          transition={{
            opacity: { type: "timing" as const, duration: isOpen ? 120 : 280, delay: isOpen ? 0 : 160 },
            translateX: { type: "timing" as const, duration: isOpen ? 120 : 280, delay: isOpen ? 0 : 160 },
            backgroundColor: { type: "timing" as const, duration: 350 },
          }}
          pointerEvents={isOpen ? "none" : "auto"}>
          <CustomButton
            text="Explore"
            leftIcon={<SparkleIcon size={16} />}
            styleType="transparent"
            size="s"
            textStyle={styles.exploreButtonText}
            onHover={(isHovered) => {
              if (Platform.OS === "web") {
                setIsExploreButtonHovered(isHovered);
              }
            }}
            onPress={handleOpen}
            accessibilityLabel="Open Exploration Panel"
            title="Open Exploration Panel"
          />
        </MotiView>
      ) : (
        <MotiView
          style={styles.mobileFabContainer}
          animate={{
            opacity: isOpen ? 0 : 1,
            translateY: isOpen ? 10 : 0,
          }}
          transition={{
            opacity: { type: "timing" as const, duration: isOpen ? 120 : 280, delay: isOpen ? 0 : 160 },
            translateY: { type: "timing" as const, duration: isOpen ? 120 : 280, delay: isOpen ? 0 : 160 },
          }}
          pointerEvents={isOpen ? "none" : "auto"}>
          <CustomButton
            text=""
            leftIcon={<SparkleIcon size={18} color={colors.white} />}
            styleType="transparent"
            size="s"
            style={styles.mobileFabButton}
            onHover={(isHovered) => {
              if (Platform.OS === "web") {
                setIsExploreButtonHovered(isHovered);
              }
            }}
            onPress={handleOpen}
            accessibilityLabel="Open Exploration Panel"
            title="Open Exploration Panel"
          />
        </MotiView>
      )}
      <MotiView
        style={[styles.panel, { maxHeight: panelMaxHeight }]}
        animate={{ translateX: isOpen ? 0 : PANEL_WIDTH + spacing.md }}
        transition={{ type: "timing" as const, duration: 280 }}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <CustomText text="Exploration" style={styles.title} weight="bold" size="s" />
            <CustomText text="Dive deeper into related concepts" style={styles.subTitle} />
          </View>
          <CustomButton
            text=""
            leftIcon={<XIcon size={14} />}
            styleType="secondaryLightened"
            style={styles.closeButton}
            onPress={() => setIsOpen(false)}
            accessibilityLabel="Close exploration menu"
          />
        </View>

        <CustomText text="Media" size="xs" weight="bold" style={styles.mediaLabel} />

        <ScrollView
          style={[styles.content, { maxHeight: contentMaxHeight }]}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator
          bounces={false}>
          {hasItems ? (
            items.map((item) => (
              <ExplorationMenuItem
                key={item.id}
                item={item}
                isHovered={hoveredItemId === item.id}
                onPress={handleItemPress(item)}
                onHoverIn={handleItemHoverIn(item.id)}
                onHoverOut={handleItemHoverOut}
              />
            ))
          ) : (
            <CustomText text="No media yet." size="s" style={styles.emptyState} useTranslationText={false} />
          )}
        </ScrollView>
      </MotiView>
      {selectedMedia ? (
        <ExplorationMediaModal
          visible={Boolean(selectedMedia)}
          onClose={() => setSelectedMedia(null)}
          type={selectedMedia.type}
          videoId={selectedMedia.type === "video" ? selectedMedia.videoId : undefined}
          src={selectedMedia.type === "image" ? selectedMedia.src : undefined}
          title={selectedMedia.title}
          description={selectedMedia.description}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 88,
    zIndex: 2,
    alignItems: "flex-end",
  },
  exploreButton: {
    alignItems: "flex-end",
    marginTop: spacing.sm,
    marginRight: spacing.sm,
  },
  mobileFabContainer: {
    position: "absolute",
    bottom: spacing["4xl"] + spacing.sm,
    right: 16,
    zIndex: 3,
  },
  exploreButtonCta: {
    paddingHorizontal: 4,
    borderRadius: radii.sm,
    shadowColor: "rgb(7, 11, 19)",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  exploreButtonText: {
    color: colors.grey[700],
  },
  mobileFabButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    paddingHorizontal: 0,
    paddingVertical: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.purple[600],
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  panel: {
    position: "absolute",
    right: spacing.sm,
    top: spacing.sm,
    width: PANEL_WIDTH,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    overflow: "hidden",
    ...shadowStyles.sm,
  },
  header: {
    paddingTop: 16,
    paddingHorizontal: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    justifyContent: "space-between",
    gap: spacing.xs,
  },
  headerText: {
    flex: 1,
  },
  title: {
    lineHeight: 21,
    color: colors.grey[900],
  },
  subTitle: {
    fontSize: 10,
    lineHeight: 15,
    color: colors.grey[600],
  },
  closeButton: {
    padding: 6,
    alignSelf: "flex-start",
  },
  content: {
    maxHeight: PANEL_MAX_HEIGHT,
  },
  contentContainer: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    gap: 12,
  },
  mediaLabel: {
    paddingHorizontal: 20,
    paddingTop: 20,
    color: colors.grey[700],
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  emptyState: {
    color: colors.grey[600],
  },
});

export default ExplorationMenu;
